import { HELP_MSG, NOTICE_MSG, SUPPORT_CATEGORIES, SUPPORT_LIMITS, SUPPORT_MSG, noticeTone, type Announcement, type HelpIndexArticle, type AnnouncementsResponse, type SupportAttachment, type SupportCategory, type SupportTicketRequest } from "@genbaryoku/contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import { api, messageOf } from "../api.js";
import { prepareImage } from "../lib/image.js";
import { captureScreen, screenLabel, type ScreenShot } from "../lib/screenCapture.js";
import { currentTrail, screenPath } from "../lib/trail.js";
import { useImageViewer } from "./ImageViewer.jsx";
import { currentPageName } from "./BackButton.jsx";
import { ErrorText, Field, Help, guardComposingEnter } from "./ui.jsx";
import { HelpPanel, useHelpIndex } from "./HelpPanel.jsx";
import { t } from "../labels.js";

/**
 * 問い合わせ（#124 の 3a、docs/50 §2.1）。
 *   SupportDialog＝ログイン後の上の帯の「？」の窓（種類・内容・画像）。会社・人・メール・画面・端末は API と窓が添える
 *     ＋「？」を押した瞬間の画面の写し（lib/screenCapture.ts。窓に見本と「この画面の写しを添える」＝既定オン・外せる。docs/50 §7）
 *   SupportForm＝窓と /contact の中身（ログイン前はメールアドレス・お名前も）
 *   AnnouncementBanner＝ホームの上のお知らせの帯（閉じたら出ない）
 * 画像は 1枚 5MB・合わせて 10MB・3枚まで（contract の SUPPORT_LIMITS）。大きい写真はこの端末で 2400px・2MB に縮める
 */

export type Picked = { name: string; type: SupportAttachment["type"]; data: string; bytes: number; url: string };

/** 頭の数バイトで種類を見る（API も同じように確かめ直す） */
function sniff(b: Uint8Array): SupportAttachment["type"] | null {
  if (b.length >= 8 && b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47) return "image/png";
  if (b.length >= 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return "image/jpeg";
  const s = String.fromCharCode(...b.slice(0, 12));
  if (/^GIF8[79]a/.test(s)) return "image/gif";
  if (s.startsWith("RIFF") && s.slice(8, 12) === "WEBP") return "image/webp";
  return null;
}

function base64Of(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => (typeof r.result === "string" ? resolve(r.result.replace(/^data:[^,]*,/, "")) : reject(new Error("read")));
    r.onerror = () => reject(r.error ?? new Error("read"));
    r.readAsDataURL(blob);
  });
}

const KEEP_BYTES = 2.5 * 1024 * 1024;

/** 送れる形にする。読めない・種類が違うときは文（間違いの文）を返す */
export async function toAttachment(file: File): Promise<Picked | string> {
  const kind = sniff(new Uint8Array(await file.slice(0, 16).arrayBuffer()));
  let blob: Blob = file;
  let type = kind;
  // GIF・WebP は描き直さない（動きが消える）。PNG・JPEG は大きければ縮める。ほか（HEIC など）は読めれば描き直す
  if (!(kind === "image/gif" || kind === "image/webp") || file.size > SUPPORT_LIMITS.fileBytes) {
    if (!(kind === "image/png" || kind === "image/jpeg") || file.size > KEEP_BYTES) {
      const p = await prepareImage(file, { maxPx: 2400 }).catch(() => null);
      if (!p) return SUPPORT_MSG.fileType;
      blob = p.blob;
      type = p.contentType;
    }
  }
  if (!type) return SUPPORT_MSG.fileType;
  if (blob.size > SUPPORT_LIMITS.fileBytes) return SUPPORT_MSG.fileTooBig;
  const name = (file.name || "画像").replace(/[\\/\0\r\n]/g, "_").slice(0, 120);
  return { name, type, data: await base64Of(blob), bytes: blob.size, url: URL.createObjectURL(blob) };
}

export { screenPath } from "../lib/trail.js";

/**
 * 画像を選ぶ（窓・/contact・返事のリンクのページで同じ）。3枚・1枚 5MB・合わせて 10MB。
 * node＝見本と「削除」と「画像選択」。clear＝送れたあと。見本（blob:）は外す・送る・閉じるときに捨てる
 */
export function useImagePicks(onError: (msg: string | undefined) => void, busy: boolean) {
  const [files, setFiles] = useState<Picked[]>([]);
  const [reading, setReading] = useState(false);
  const ref = useRef(files);
  ref.current = files;
  useEffect(() => () => ref.current.forEach((f) => URL.revokeObjectURL(f.url)), []);
  const add = async (list: FileList | null) => {
    if (!list?.length) return;
    onError(undefined);
    setReading(true);
    try {
      const room = SUPPORT_LIMITS.files - ref.current.length;
      const picked: Picked[] = [];
      let total = ref.current.reduce((s, f) => s + f.bytes, 0);
      for (const f of Array.from(list).slice(0, Math.max(0, room))) {
        const r = await toAttachment(f);
        if (typeof r === "string") {
          onError(r);
          continue;
        }
        if (total + r.bytes > SUPPORT_LIMITS.totalBytes) {
          URL.revokeObjectURL(r.url);
          onError(SUPPORT_MSG.fileTooBig);
          continue;
        }
        total += r.bytes;
        picked.push(r);
      }
      if (list.length > room) onError(`画像は ${SUPPORT_LIMITS.files}枚までです`);
      if (picked.length) setFiles((x) => [...x, ...picked]);
    } finally {
      setReading(false);
    }
  };
  const drop = (i: number) =>
    setFiles((x) => {
      const f = x[i];
      if (f) URL.revokeObjectURL(f.url);
      return x.filter((_, j) => j !== i);
    });
  const clear = () => {
    ref.current.forEach((f) => URL.revokeObjectURL(f.url));
    setFiles([]);
  };
  const off = busy || reading;
  const node = (
    <div className="supfiles">
      <span className="suplabel">{SUPPORT_MSG.files}</span>
      {files.length ? (
        <ul>
          {files.map((f, i) => (
            <li key={f.url}>
              <img src={f.url} alt={f.name} />
              <button type="button" className="btn text" onClick={() => drop(i)} aria-label={`${f.name} を削除`} disabled={off}>
                削除
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {files.length < SUPPORT_LIMITS.files ? (
        // ファイルを選ぶのは入力欄の <label>（display:none の input を click() で開かない＝Safari。docs/10 §5.10）
        <label className={"btn filebtn" + (off ? " disabled" : "")}>
          {reading ? "読み込んでいます…" : "画像選択"}
          <input type="file" accept="image/*" multiple className="vh" disabled={off} data-testid="support-file" onChange={(e) => { void add(e.target.files); e.target.value = ""; }} />
        </label>
      ) : null}
    </div>
  );
  const attachments = files.length ? { attachments: files.map(({ name, type, data }) => ({ name, type, data })) } : {};
  return { files, reading, clear, node, attachments };
}

/** 画面の写しの出来上がりを待つ（窓が閉じても写しの URL は SupportButton が捨てる） */
function useShot(p: Promise<ScreenShot | null> | null | undefined): { pending: boolean; shot: ScreenShot | null } {
  const [s, setS] = useState<{ pending: boolean; shot: ScreenShot | null }>(() => ({ pending: !!p, shot: null }));
  useEffect(() => {
    if (!p) return;
    let alive = true;
    void p.then((shot) => alive && setS({ pending: false, shot }));
    return () => {
      alive = false;
    };
  }, [p]);
  return s;
}

/** ロボットよけ：人には見えない欄（入っていたら API が受けたふりをして捨てる） */
export function Honeypot({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const id = useId();
  return (
    <div className="vh" aria-hidden="true">
      <label htmlFor={id}>website</label>
      <input id={id} type="text" name="website" tabIndex={-1} autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

type Draft = { category: SupportCategory | ""; body: string; email: string; name: string; website: string };
const EMPTY: Draft = { category: "", body: "", email: "", name: "", website: "" };

/**
 * 問い合わせの中身（窓と /contact で同じ）。guest＝ログイン前（メールアドレス・お名前を聞く）。
 * 送れたら onSent。見えない欄 website はロボットだけが入れる（人には見えず、読み上げもしない）。
 * shot＝「？」を押したときの画面の写し（窓だけ）。作っているあいだに「送信」を押したら、できるのを待ってから送る
 */
export function SupportForm({ guest, onSent, footer, autoFocus, bodyClass = "supfields", shot, note }: { guest: boolean; onSent: () => void; footer: (s: { busy: boolean }) => React.ReactNode; autoFocus?: boolean; bodyClass?: string; shot?: Promise<ScreenShot | null> | null | undefined; /** ヘルプの記事で「いいえ」と答えたとき（#150）：上に一文を出して、種類の欄へ */ note?: string | undefined }) {
  const [d, setD] = useState<Draft>(EMPTY);
  const cap = useShot(shot);
  const viewer = useImageViewer();
  const [withShot, setWithShot] = useState(true);
  const [waiting, setWaiting] = useState(false);
  /** 送っている最中（2回押しても1通。描き直しの前の2回目も止める） */
  const sending = useRef(false);
  const [err, setErr] = useState<string | undefined>();
  const [errs, setErrs] = useState<Partial<Record<"category" | "body" | "email", string>>>({});
  const base = useId();
  const id = (k: string) => `${base}-${k}`;
  const first = useRef<HTMLSelectElement>(null);
  const set = (p: Partial<Draft>) => setD((x) => ({ ...x, ...p }));
  useEffect(() => {
    if (autoFocus) first.current?.focus();
  }, [autoFocus]);
  useEffect(() => {
    if (!note) return;
    first.current?.focus({ preventScroll: true });
    first.current?.closest(".supform")?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [note]);

  const send = useMutation({
    mutationFn: (body: SupportTicketRequest) => api<{ ok: true }>("POST", "/support/tickets", body),
    onSuccess: () => {
      pics.clear();
      setD(EMPTY);
      onSent();
    },
    onError: (e) => setErr(messageOf(e)),
  });
  const pics = useImagePicks(setErr, send.isPending);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending.current || send.isPending || pics.reading || waiting) return;
    const es: typeof errs = {};
    if (guest && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim())) es.email = d.email.trim() ? "メールアドレスの形を確かめてください" : "メールアドレスを入れてください";
    if (!d.category) es.category = "種類を選んでください";
    if (!d.body.trim()) es.body = "内容を書いてください";
    else if (d.body.length > SUPPORT_LIMITS.bodyMax) es.body = `内容は ${SUPPORT_LIMITS.bodyMax}字までです`;
    setErrs(es);
    const firstErr = Object.values(es)[0];
    if (firstErr) {
      setErr(firstErr);
      return;
    }
    setErr(undefined);
    // 写しができるのを待つあいだと base64 にするあいだも、もう一度押しても送らない
    sending.current = true;
    setWaiting(true);
    let capture: { capture?: { type: ScreenShot["type"]; data: string } } = {};
    try {
      const s = shot && withShot ? await shot : null;
      if (s) capture = { capture: { type: s.type, data: await base64Of(s.blob) } };
    } catch {
      capture = {};
    } finally {
      setWaiting(false);
    }
    const screen = screenPath();
    send.mutate({
      category: d.category as SupportCategory,
      body: d.body.trim(),
      ...(guest ? { email: d.email.trim(), ...(d.name.trim() ? { name: d.name.trim() } : {}) } : {}),
      screen,
      // 1件の画面（顧客・案件の詳細など）の名前は顧客名・案件名なので送らない（画面はパスの :id で分かる）
      title: /:(id|n)\b/.test(screen) ? "" : (currentPageName() ?? document.title).slice(0, 120),
      width: Math.round(window.innerWidth),
      ...pics.attachments,
      ...capture,
      // 画面の版と直前の操作（ログイン後だけ。#144、docs/53 §8.4）
      ...(guest ? {} : { trail: currentTrail() }),
      ...(d.website ? { website: d.website } : {}),
    }, { onSettled: () => { sending.current = false; } });
  };

  const busy = send.isPending || pics.reading || waiting;
  const showShot = !!shot && (cap.pending || !!cap.shot);
  return (
    <form className="supform" onSubmit={(e) => void submit(e)} noValidate data-testid="support-form">
      <div className={bodyClass}>
        {note ? (
          <p className="supnote" role="status" data-testid="support-help-note">
            {note}
          </p>
        ) : null}
        {guest ? (
          <>
            <Field id={id("email")} label={<>{SUPPORT_MSG.email}<span className="req">必須</span></>}>
              <input id={id("email")} className="inp" type="email" autoComplete="email" inputMode="email" spellCheck={false} autoCapitalize="off" maxLength={254} value={d.email} onChange={(e) => set({ email: e.target.value })} aria-invalid={!!errs.email} onKeyDown={guardComposingEnter} />
            </Field>
            <Field id={id("name")} label={SUPPORT_MSG.name}>
              <input id={id("name")} className="inp" type="text" autoComplete="name" maxLength={60} value={d.name} onChange={(e) => set({ name: e.target.value })} onKeyDown={guardComposingEnter} />
            </Field>
          </>
        ) : null}
        <Field id={id("cat")} label={<>{SUPPORT_MSG.category}<span className="req">必須</span></>}>
          <select id={id("cat")} ref={first} className="inp" value={d.category} onChange={(e) => set({ category: e.target.value as SupportCategory })} aria-invalid={!!errs.category} data-testid="support-category">
            <option value="">（選ぶ）</option>
            {Object.entries(SUPPORT_CATEGORIES).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field id={id("body")} label={<>{SUPPORT_MSG.body}<span className="req">必須</span></>}>
          <textarea id={id("body")} className="inp supbody" rows={6} maxLength={SUPPORT_LIMITS.bodyMax} value={d.body} onChange={(e) => set({ body: e.target.value })} aria-invalid={!!errs.body} data-testid="support-body" />
        </Field>
        {showShot ? (
          <div className="supshot" data-testid="support-shot">
            <label className="chk">
              <input type="checkbox" checked={withShot} disabled={busy} onChange={(e) => setWithShot(e.target.checked)} data-testid="support-shot-check" />
              <span>{SUPPORT_MSG.capture}</span>
            </label>
            {cap.shot ? (
              withShot ? (
                <button type="button" className="supthumb" onClick={() => cap.shot && viewer.open([{ src: cap.shot.url, name: cap.shot.name }])} aria-label={`${SUPPORT_MSG.captureAlt}を大きく表示`}>
                  <img src={cap.shot.url} alt={SUPPORT_MSG.captureAlt} data-testid="support-shot-img" />
                </button>
              ) : null
            ) : (
              // 写しが届くまでも同じ大きさの枠（#179：写しが入った瞬間に窓が伸び、種類・内容の欄・「送信」が上下にずれていた）
              <span className="supthumb is-wait" data-testid="support-shot-wait">{SUPPORT_MSG.capturing}</span>
            )}
          </div>
        ) : null}
        {pics.node}
        {viewer.node}
        <Honeypot value={d.website} onChange={(v) => set({ website: v })} />
        {guest ? null : <p className="hint supauto">{showShot && withShot ? SUPPORT_MSG.autoShot : SUPPORT_MSG.auto}</p>}
        <ErrorText>{err}</ErrorText>
      </div>
      {footer({ busy })}
    </form>
  );
}

/**
 * ログイン後の「ヘルプ」の窓。Esc・閉じる・後ろで閉じ、閉じたら押したボタンに戻る。窓は画面の写しに入らない（data-nocapture）。
 * article＝開いたときにその記事を開く（#206：「お知らせ」の「ヘルプで見る」）
 */
export function SupportDialog({ onClose, shot, article }: { onClose: () => void; shot?: Promise<ScreenShot | null> | null; article?: string | null }) {
  const [sent, setSent] = useState(false);
  const titleId = useId();
  // ヘルプの記事（#150）：公開の記事が1本も無ければ上半分を出さない（題も「お問い合わせ」のまま）
  const help = useHelpIndex();
  const hasHelp = (help.data?.articles ?? []).some((a) => a.status === "published");
  // ヘルプの索引が届くまで中身を待つのは 1.5 秒まで（#179：索引の取り寄せが止まっても、問い合わせはできるように）
  const [helpWaitOver, setHelpWaitOver] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setHelpWaitOver(true), 1500);
    return () => window.clearTimeout(t);
  }, []);
  const [notSolved, setNotSolved] = useState<HelpIndexArticle | null>(null);
  const [screen] = useState(() => screenPath());
  const busyRef = useRef(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busyRef.current) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="dim" data-testid="support-dialog" data-nocapture onMouseDown={(e) => e.target === e.currentTarget && !busyRef.current && onClose()}>
      <div className="dlg supdlg" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="dh" id={titleId}>
          {hasHelp ? HELP_MSG.dialogTitle : SUPPORT_MSG.title}
          <Help text={SUPPORT_MSG.hours} label="お問い合わせの説明" />
        </div>
        {help.isPending && !helpWaitOver ? (
          // ヘルプの索引が届くまでは中身を描かない（#179：先に欄を出すと、索引が届いて上半分の記事が入った瞬間に欄がずれる）
          <div className="db">
            <p className="hint" style={{ margin: 0 }} data-testid="support-loading">読み込んでいます…</p>
          </div>
        ) : sent ? (
          <>
            <div className="db">
              <p role="status" data-testid="support-sent" style={{ margin: 0 }}>
                {SUPPORT_MSG.sent}
              </p>
            </div>
            <div className="df">
              <span className="sp" />
              <button type="button" className="btn primary" onClick={onClose} autoFocus>
                {SUPPORT_MSG.close}
              </button>
            </div>
          </>
        ) : (
          <>
          {hasHelp ? (
            <div className="db hlpwrap">
              <HelpPanel index={help.data} screen={screen} onNotSolved={setNotSolved} initialArticle={article} />
            </div>
          ) : null}
          <SupportForm
            guest={false}
            autoFocus={!hasHelp}
            bodyClass="db"
            shot={shot}
            note={notSolved ? HELP_MSG.notSolved(t(notSolved.title)) : undefined}
            onSent={() => setSent(true)}
            footer={({ busy }) => {
              busyRef.current = busy;
              return (
                <div className="df">
                  <button type="button" className="btn text" onClick={onClose} disabled={busy}>
                    {SUPPORT_MSG.close}
                  </button>
                  <span className="sp" />
                  <button type="submit" className="btn primary" disabled={busy} data-testid="support-send">
                    {SUPPORT_MSG.send}
                  </button>
                </div>
              );
            }}
          />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * 「ヘルプ」の窓（ヘルプの記事＋お問い合わせ。ログイン後）。押した瞬間の画面を写してから窓を開き、閉じたら写しの URL を捨てて押したボタンへ戻る。
 * #187（10/15 射場さん）：入口は上の帯の「？」から左の列のいちばん下（「縮小」の上）とスマホの ☰ のメニューのいちばん下へ。
 * 上の帯は本文と一緒にスクロールして消え、表を下へ送った所で押せず、見えている範囲の写しが撮れなかった。左の列の下は sticky なので、どこまで送っても押せる。
 * 窓は枠（Shell）に1つ。入口は2つ（左の列・☰）で、どちらも open を呼ぶ
 */
export function useSupport() {
  const [shot, setShot] = useState<Promise<ScreenShot | null> | null>(null);
  /** 開いたときに開く記事（#206：「お知らせ」の「ヘルプで見る」） */
  const [article, setArticle] = useState<string | null>(null);
  // ヘルプの索引は先に読んでおく（窓を開いた瞬間に上半分が出るように。30分は読み直さない）
  useHelpIndex();
  const back = useRef<HTMLElement | null>(null);
  const live = useRef(shot);
  live.current = shot;
  // 窓を開いたまま画面ごと閉じた（ログアウト・ほかの画面へ）ときも写しの URL を捨てる
  useEffect(() => () => void live.current?.then((s) => s && URL.revokeObjectURL(s.url)), []);
  /** from＝閉じたあとにフォーカスを戻す所（押したボタン。☰ から開いたときは ☰）。articleId＝開いたらその記事を開く（#206） */
  const open = (from?: HTMLElement | null, articleId?: string) => {
    back.current = from ?? null;
    setArticle(articleId ?? null);
    setShot(captureScreen(screenLabel(location.pathname, currentPageName())));
  };
  const close = () => {
    void shot?.then((s) => s && URL.revokeObjectURL(s.url));
    setShot(null);
    requestAnimationFrame(() => back.current?.focus());
  };
  return { open, dialog: shot ? <SupportDialog onClose={close} shot={shot} article={article} /> : null };
}

/**
 * 「ヘルプ」の項目（#187）：左の列（44px・縮小のときはアイコンだけ）と ☰ のメニュー（56px）。読み上げは「ヘルプ・お問い合わせ」。
 * 更新情報の未読の朱の点は「お知らせ」に付ける（#206。前は #204 でここに付けていた）
 */
export function HelpNavButton({ onOpen }: { onOpen: (el: HTMLButtonElement) => void }) {
  return (
    <button type="button" className="navhelp" aria-label={SUPPORT_MSG.open} title={SUPPORT_MSG.open} aria-haspopup="dialog" onClick={(e) => onOpen(e.currentTarget)} data-testid="support-open">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.4-1 1-1 1.7M12 17h.01" />
      </svg>
      <span data-t={SUPPORT_MSG.navHelp}>{SUPPORT_MSG.navHelp}</span>
    </button>
  );
}

/** お知らせ（ホームの帯）。ホームは帯とカードを一緒に描くため、これが届くのを待つ（#179） */
export function useAnnouncements() {
  return useQuery({ queryKey: ["announcements"], queryFn: () => api<AnnouncementsResponse>("GET", "/announcements"), staleTime: 5 * 60_000, retry: false });
}

/**
 * ホームの上の案内の帯（緊急 → 注意 → メンテナンス → お知らせ・3つまで。閉じたら出ない＝閉じた人ごとに覚える）。
 * 印の文字は区分の名前（docs/95）：お知らせ・メンテナンスは藍、注意・緊急は朱（色だけで伝えない）
 */
export function AnnouncementBanner() {
  const qc = useQueryClient();
  const q = useAnnouncements();
  const read = useMutation({
    mutationFn: (id: string) => api<{ ok: true }>("POST", `/announcements/${id}/read`),
    onMutate: (id) => {
      qc.setQueryData<AnnouncementsResponse>(["announcements"], (x) => (x ? { items: x.items.filter((a) => a.id !== id) } : x));
    },
  });
  const items: Announcement[] = q.data?.items ?? [];
  if (!items.length) return null;
  return (
    <div className="anns" data-testid="announcements">
      {items.map((a) => (
        <section key={a.id} className={"ann" + (noticeTone(a.kind) === "shu" ? " warn" : "")} aria-label={NOTICE_MSG.region(a.kind)} data-testid="announcement" data-kind={a.kind}>
          <span className="annmark">{NOTICE_MSG.kind[a.kind]}</span>
          <div className="anntext">
            <b>{a.title}</b>
            {a.body ? <p>{a.body}</p> : null}
          </div>
          <button type="button" className="btn text" onClick={() => read.mutate(a.id)} aria-label={`「${a.title}」を閉じる`} data-testid="announcement-close">
            {SUPPORT_MSG.close}
          </button>
        </section>
      ))}
    </div>
  );
}
